/**
 * localStorage-backed cache for climatology responses from /api/weather.
 *
 * Climatology (monthly averages) is static per location/month, so we persist
 * with a 90-day TTL keyed by lat:lng:yyyymm. Forecast responses (source !==
 * 'climate') are not cached here — they have their own short TTL on the server.
 */

// v2: climatology responses are now Celsius-normalized (backend used to return
// Fahrenheit values keyed as `high`/`low`, which caused frontends that
// auto-C→F-converted to show 140°+ readings). Bumping the prefix invalidates
// any cached Fahrenheit payloads on client devices.
const PREFIX = 'weatherClimo:v2:';
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

function coordKey(v) {
  // 3-decimal rounding matches backend weather_climatology precision.
  return Number(parseFloat(v)).toFixed(3);
}

function yyyymmFromDate(dateStr) {
  // dateStr is 'YYYY-MM-DD'
  if (!dateStr || typeof dateStr !== 'string') return null;
  const [y, m] = dateStr.split('-');
  if (!y || !m) return null;
  return `${y}${m}`;
}

export function climatologyCacheKey(lat, lng, dateStr) {
  const ym = yyyymmFromDate(dateStr);
  if (ym == null) return null;
  return `${PREFIX}${coordKey(lat)}:${coordKey(lng)}:${ym}`;
}

export function readClimatology(lat, lng, dateStr) {
  try {
    const key = climatologyCacheKey(lat, lng, dateStr);
    if (!key || typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.expiresAt || parsed.expiresAt < Date.now()) {
      try { localStorage.removeItem(key); } catch {}
      return null;
    }
    return parsed.data || null;
  } catch {
    return null;
  }
}

export function writeClimatology(lat, lng, dateStr, data) {
  try {
    if (!data || data.source !== 'climate') return; // only cache climatology
    const key = climatologyCacheKey(lat, lng, dateStr);
    if (!key || typeof localStorage === 'undefined') return;
    localStorage.setItem(
      key,
      JSON.stringify({ data, expiresAt: Date.now() + TTL_MS })
    );
  } catch {
    // Quota exceeded or localStorage disabled — ignore
  }
}
